// src/store/slices/labelVisibilitySlice.ts
/**
 * Label visibility overrides for individual tick labels
 * Implements Section 5.4 of the specification
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LabelVisibilityState } from '../../types/redux';

const initialState: LabelVisibilityState = {};

export const labelVisibilitySlice = createSlice({
  name: 'labelVisibility',
  initialState,
  reducers: {
    /**
     * Toggle visibility of a specific label
     * @param valueKey - String representation of the value
     */
    toggleLabelVisibility: (state, action: PayloadAction<string>) => {
      const valueKey = action.payload;
      if (state[valueKey]) {
        state[valueKey].hidden = !state[valueKey].hidden;
      } else {
        state[valueKey] = { hidden: true };
      }
    },

    /**
     * Set visibility of a specific label
     */
    setLabelVisibility: (state, action: PayloadAction<{ valueKey: string; hidden: boolean }>) => {
      const { valueKey, hidden } = action.payload;
      const eqMatch = valueKey.startsWith('eq:') ? parseInt(valueKey.split(':')[1], 10) : undefined;
      state[valueKey] = { hidden, equivalentDenominator: Number.isFinite(eqMatch) ? eqMatch : undefined };
    },

    /**
     * Show all labels (clear all overrides)
     */
    showAllLabels: (state) => {
      Object.keys(state).forEach(key => {
        state[key].hidden = false;
      });
    },

    /**
     * Hide specific set of labels
     */
    hideLabels: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach(valueKey => {
        state[valueKey] = { hidden: true };
      });
    },

    /** Remove every individual override (used when toggling Hide‑All) */
    clearVisibilityOverrides: (state) => {
      Object.keys(state).forEach((k) => delete state[k]);
    },

    /** One-time migration: clear any secondary label overrides (old or new key styles) */
    clearSecondaryLabelOverrides: (state) => {
      const keys = Object.keys(state);
      for (const key of keys) {
        if (key.startsWith('secondary-') || key.startsWith('secondary:')) {
          delete state[key];
        }
      }
    },

    /**
     * Load label visibility state from shared scene
     */
    loadLabelVisibilityState: (state, action: PayloadAction<LabelVisibilityState>) => {
      // Clear existing state
      Object.keys(state).forEach(key => delete state[key]);
      // Apply loaded state
      Object.assign(state, action.payload);
    },
  },
});

export const { 
  toggleLabelVisibility,
  setLabelVisibility, 
  clearVisibilityOverrides,
  clearSecondaryLabelOverrides,
  loadLabelVisibilityState,
} = labelVisibilitySlice.actions;

export default labelVisibilitySlice.reducer;